/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { timeout, restartableTask } from 'ember-concurrency';
import Ember from 'ember';
import { IntegrationMethod, IntegrationStatus } from 'cio/models/setup-guide';
import FacebookLeadAdsService, { FacebookBusinessPageData } from 'cio/services/facebook-lead-ads';
import config from 'cio/config/environment';
import { unixNowSeconds } from 'cio/utils/time';
import RouterService from '@ember/routing/router-service';
import { tracked } from '@glimmer/tracking';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import { task } from 'ember-concurrency';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import AdBlockerService from 'cio/services/ad-blocker';
import { isEmpty, isNone } from '@ember/utils';
import ApiErrorsService from 'cio/services/api-errors';

interface FormsSetupFormsFacebookLeadAdsArgs {
  setupGuideOnSelect?: (method: IntegrationMethod, connectedAt: number) => void;
  setupGuideStatus?: IntegrationStatus;
  onConnectForm?: (form: FacebookLeadAdsForm) => unknown;
  source: string;
  isBold?: boolean;
}

export default class FormsSetupFormsFacebookLeadAds extends Component<FormsSetupFormsFacebookLeadAdsArgs> {
  @service auth!: AuthService;
  @service adBlocker!: AdBlockerService;
  @service alerts!: AlertsService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;

  // relevant for connecting to a Facebook business page (enabling Lead Ads integration):
  @tracked userAccessToken: string | null = null;
  @tracked facebookAccountName: string | null = null;
  @tracked pages: FacebookBusinessPageData[] | null = null;

  // relevant to connecting a Facebook form
  @tracked isConnectingNewForm = false;
  @tracked facebookLeadAdsFormToConnect: FacebookLeadAdsForm | null = null;

  fbNextPageCursor = '';
  facebookImage = new URL('cio/assets/images/integrations/facebook.svg', import.meta.url).toString();

  get isDisabled() {
    return (
      !this.facebookLeadAds.isAvailable ||
      (this.facebookLeadAds.enabled && this.facebookLeadAds.connectableDisconnectedForms.length === 0) ||
      this.isDisabledBecauseOfAdBlocker
    );
  }

  get isDisabledBecauseOfAdBlocker() {
    return !this.facebookLeadAds.enabled && this.adBlocker.isFacebookBlocked;
  }

  @action
  reportConnection() {
    if (this.args.setupGuideStatus == null || this.args.setupGuideOnSelect == null) {
      return;
    }

    if (this.args.setupGuideStatus !== 'complete' && this.facebookLeadAds.enabled) {
      this.args.setupGuideOnSelect('forms', unixNowSeconds());
    }
  }

  constructor(owner: unknown, args: FormsSetupFormsFacebookLeadAdsArgs) {
    super(owner, args);

    this.facebookLeadAds.initialize();
  }

  @action
  clickAction() {
    if (!this.facebookLeadAds.enabled) {
      return this.setupIntegration.perform();
    }

    this.isConnectingNewForm = true;
    return;
  }

  @action
  onConnectForm(form: FacebookLeadAdsForm) {
    return this.args.onConnectForm?.(form);
  }

  @action
  cancelConnectForm() {
    this.isConnectingNewForm = false;
    this.facebookLeadAdsFormToConnect = null;
  }

  pollForUpdates = restartableTask(async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Not initialized yet, start from scratch
      if (!this.facebookLeadAds.initialized) {
        await this.facebookLeadAds.initialize();
        // Initialized, but no credential found yet. Start by trying to find a credential first
      } else if (!this.facebookLeadAds.enabled) {
        let credential = await this.facebookLeadAds.fetchCredential();

        // If a credential was found for the first time, fetch any forms that might exist now as well
        if (credential) {
          await this.facebookLeadAds.fetchForms();
        }
        // Initialized and connected already, just fetch new forms
      } else {
        await this.facebookLeadAds.fetchForms();
      }

      // break for tests since this causes a render loop that never resolves
      if (Ember.testing) {
        break;
      }

      await timeout(config.APP.pollingDelay);
    }
  });

  setupIntegration = task(async () => {
    try {
      let { userAccessToken, pages, facebookAccountName, morePages } =
        await this.facebookLeadAds.setupStart('Setup Guide');
      Object.assign(this, { userAccessToken, pages, facebookAccountName });
      if (!isNone(morePages)) {
        this.fbNextPageCursor = morePages;
      }
    } catch (e) {
      // ignore: a re-thrown error already handled by facebookLeadAds service
    }
  });

  setupFinishTask = task(async (facebookPageID: string) => {
    let { userAccessToken } = this;
    if (!userAccessToken) {
      return;
    }

    await this.facebookLeadAds.setupFinish({
      userAccessToken,
      pageId: facebookPageID,
      from: 'Setup Guide',
    });

    // setup is done, drop tmp. properties used for integration setup
    this.userAccessToken = null;
    this.pages = null;
    this.facebookAccountName = null;

    // open "Connect form" modal if there are any available forms to connect:
    if (this.facebookLeadAds.usableForms.length > 0) {
      this.isConnectingNewForm = true;
    }
  });

  fetchMorePagesTask = task(async () => {
    if (!isEmpty(this.fbNextPageCursor)) {
      try {
        let { pages, morePages } = await this.facebookLeadAds.fetchMorePages(this.fbNextPageCursor);
        this.pages = [...(this.pages ?? []), ...pages];
        if (!isNone(morePages)) {
          this.fbNextPageCursor = morePages;
        }
        this.fbNextPageCursor = '';
      } catch (err) {
        this.apiErrors.report(err);
      }
    }
  });
}
